// Massenimport-Dialog für unbekannte Superblöcke (.SBL) — Issue #271.
//
// Ausgangslage: der geöffnete Graph (oder ein importiertes Bundle) referenziert Superblöcke, die
// weder als Default noch als Import in der Bibliothek liegen. Der Dialog nimmt MEHRERE .SBL auf und
// spielt sie über den Bulk-Import ein. Verschachtelte Superblöcke (ein .SBL referenziert weitere)
// lösen sich über MEHRERE Durchläufe auf: nach jedem Upload meldet das Backend in `stillMissing`,
// was — inkl. der neu aufgedeckten verschachtelten Refs — noch fehlt. Der Fortschritt „x von y
// aufgelöst, noch fehlend: …" bleibt sichtbar, bis nichts mehr offen ist.

import { useCallback, useMemo, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { importSuperblocks } from '../api/client';
import type { SuperblockImportResult } from '../api/types';
import { Button, PanelHeading } from '../components/ui';

export interface SuperblockBulkImportDialogProps {
  // Die anfangs unbekannten (fehlenden) Superblock-Refs — der Startzustand der Auflösung.
  initialMissing: string[];
  onClose: () => void;
  // Nach jedem erfolgreichen Durchlauf: Palette neu laden (der Aufrufer aktualisiert seinen Zustand).
  onImported: () => void | Promise<void>;
}

export function SuperblockBulkImportDialog({
  initialMissing,
  onClose,
  onImported,
}: SuperblockBulkImportDialogProps): JSX.Element {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);

  // Die Gesamtmenge, die aufgelöst werden soll, wächst um verschachtelte Refs, die erst beim Import
  // sichtbar werden. `resolvedCount` = total − noch fehlend.
  const [missing, setMissing] = useState<string[]>(() => [...initialMissing]);
  const [total, setTotal] = useState<number>(initialMissing.length);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SuperblockImportResult | null>(null);
  const [passes, setPasses] = useState(0);

  const resolved = useMemo(() => Math.max(0, total - missing.length), [total, missing.length]);
  const done = missing.length === 0;

  const runImport = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setBusy(true);
      setError(null);
      try {
        // Die aktuell offenen Refs als `expected` mitgeben → das Backend rechnet `stillMissing`
        // (inkl. der von den gerade importierten Dateien verschachtelt referenzierten).
        const res = await importSuperblocks(files, missing);
        setLastResult(res);
        setPasses((p) => p + 1);
        const still = res.stillMissing ?? [];
        // Neu aufgedeckte, bislang nicht gezählte Refs erhöhen die Gesamtmenge (verschachtelt).
        setTotal((prev) => {
          const known = new Set<string>();
          for (const r of missing) known.add(r.toLowerCase());
          let grown = prev;
          for (const r of still) if (!known.has(r.toLowerCase())) grown += 1;
          return Math.max(prev, grown);
        });
        setMissing(still);
        await onImported();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [missing, onImported],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('signal.bulkImport.title')}
      data-testid="superblock-bulk-dialog"
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-lg border border-bf-border bg-bf-navy p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-bf-white">{t('signal.bulkImport.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            data-testid="superblock-bulk-close"
            onClick={onClose}
            className="text-bf-muted hover:text-bf-white"
          >
            ✕
          </button>
        </div>

        <p className="text-xs text-bf-muted">{t('signal.bulkImport.intro')}</p>

        {/* Fortschritt: x von y aufgelöst. */}
        <div
          className="flex flex-col gap-1 rounded border border-bf-border bg-bf-navy-light/40 p-2"
          data-testid="superblock-bulk-progress"
        >
          <span className="text-sm text-bf-white">
            {t('signal.bulkImport.progress', { resolved, total })}
          </span>
          <div className="h-1.5 w-full overflow-hidden rounded bg-bf-border">
            <div
              className="h-full bg-bf-gold transition-all"
              style={{ width: total > 0 ? `${(resolved / total) * 100}%` : '100%' }}
            />
          </div>
        </div>

        {/* Noch fehlende Superblöcke — die „noch fehlend: …"-Liste. */}
        {!done && (
          <div
            className="flex flex-col gap-1 rounded border border-amber-500/50 bg-amber-950/20 p-2"
            data-testid="superblock-bulk-missing"
          >
            <PanelHeading>{t('signal.bulkImport.stillMissing')}</PanelHeading>
            <ul className="max-h-40 overflow-y-auto text-xs text-amber-200">
              {missing.map((ref) => (
                <li key={ref} className="truncate" title={ref}>
                  {ref}
                </li>
              ))}
            </ul>
          </div>
        )}

        {done && (
          <div
            className="rounded border border-green-500/50 bg-green-950/20 p-2 text-sm text-green-300"
            data-testid="superblock-bulk-done"
          >
            {t('signal.bulkImport.allResolved')}
          </div>
        )}

        {/* Ergebnis des letzten Durchlaufs: übersprungene Dateien mit Grund (z. B. Dublette). */}
        {lastResult && lastResult.skipped.length > 0 && (
          <div className="flex flex-col gap-1 rounded border border-bf-border p-2 text-xs">
            <PanelHeading>{t('signal.bulkImport.skipped')}</PanelHeading>
            <ul className="max-h-32 overflow-y-auto text-bf-muted" data-testid="superblock-bulk-skipped">
              {lastResult.skipped.map((s) => (
                <li key={s.filename} className="truncate">
                  {s.filename}: {s.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-300" data-testid="superblock-bulk-error">
            {error}
          </p>
        )}

        {/* Datei-Auswahl (mehrere .SBL) + Aktion. */}
        <input
          ref={fileRef}
          type="file"
          accept=".sbl"
          multiple
          className="hidden"
          data-testid="superblock-bulk-input"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            void runImport(files);
          }}
        />
        <div className="flex flex-wrap items-center justify-end gap-2">
          {!done && (
            <span data-testid="superblock-bulk-select">
              <Button variant="primary" disabled={busy} onClick={() => fileRef.current?.click()}>
                {busy
                  ? t('signal.bulkImport.busy')
                  : passes > 0
                    ? t('signal.bulkImport.selectMore')
                    : t('signal.bulkImport.select')}
              </Button>
            </span>
          )}
          <Button onClick={onClose}>{done ? t('common.close') : t('signal.bulkImport.later')}</Button>
        </div>
      </div>
    </div>
  );
}
